'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  Smartphone,
  Monitor,
  Palette,
  Image as ImageIcon,
  Check,
  Save,
  ExternalLink,
  Sparkles,
  Sun,
  Moon
} from 'lucide-react';
import { SettingsSidebar } from '@/components/dashboard/settings/SettingsSidebar';
import { ImageUploadDropzone } from '@/components/ui/ImageUploadDropzone';

const PRESET_COLORS = [
  { name: 'Esmeralda AppGenda', hex: '#0a3826' },
  { name: 'Azul Pizarra', hex: '#1e293b' },
  { name: 'Teal Marino', hex: '#0f766e' },
  { name: 'Borravino / Cuero', hex: '#7c2d12' },
  { name: 'Índigo Real', hex: '#4338ca' },
  { name: 'Carbón Puro', hex: '#0f172a' },
  { name: 'Rosa Magenta', hex: '#be185d' },
];

export default function BookingPageCustomization() {
  const [style, setStyle] = useState<number>(1); // 1: Clásico, 2: Foto grande
  const [theme, setTheme] = useState<'LIGHT' | 'DARK'>('DARK');
  const [brandColor, setBrandColor] = useState('#1e293b');
  const [logoUrl, setLogoUrl] = useState('https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop');
  const [coverUrl, setCoverUrl] = useState('https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=1000&h=450&fit=crop');
  const [welcomePhrase, setWelcomePhrase] = useState('Selecciona el servicio que mejor se adapte a tus necesidades.');
  const [businessName, setBusinessName] = useState('Juan Rojas');
  const [previewDevice, setPreviewDevice] = useState<'mobile' | 'desktop'>('mobile');

  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const fetchCustomization = useCallback(async () => {
    try {
      const res = await fetch('/api/business');
      const data = await res.json();
      if (data.success && data.business) {
        const b = data.business;
        if (b.portalStyle) setStyle(b.portalStyle);
        if (b.portalTheme) setTheme(b.portalTheme as any);
        if (b.brandColor) setBrandColor(b.brandColor);
        if (b.logoUrl) setLogoUrl(b.logoUrl);
        if (b.coverUrl) setCoverUrl(b.coverUrl);
        if (b.welcomePhrase) setWelcomePhrase(b.welcomePhrase);
        if (b.name) setBusinessName(b.name);
      }
    } catch (e) {
      console.warn('Error fetching booking page settings:', e);
    }
  }, []);

  useEffect(() => {
    fetchCustomization();
  }, [fetchCustomization]);

  const handleSave = async () => {
    setSaving(true);
    setStatusMessage(null);

    try {
      const res = await fetch('/api/business', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          portalStyle: style,
          portalTheme: theme,
          brandColor,
          logoUrl,
          coverUrl,
          welcomePhrase,
        })
      });
      const data = await res.json();
      if (data.success) {
        setStatusMessage({ text: 'Personalización guardada exitosamente.', type: 'success' });
      } else {
        setStatusMessage({ text: data.error || 'Error al guardar.', type: 'error' });
      }
    } catch (e: any) {
      setStatusMessage({ text: e.message || 'Error de conexión', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Cabecera */}
      <div className="space-y-1">
        <Link
          href="/dashboard"
          className="inline-flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Volver al inicio</span>
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
          Tu página de reservas
        </h1>
        <p className="text-xs text-slate-500">
          Personalizá el estilo, logo, portada y colores con los que te ven tus clientes.
        </p>
      </div>

      {statusMessage && (
        <div
          className={`p-4 rounded-2xl text-xs font-semibold flex items-center justify-between shadow-2xs ${
            statusMessage.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          <span>{statusMessage.text}</span>
          <button onClick={() => setStatusMessage(null)} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
      )}

      {/* Grid Principal: Sidebar, Formulario y Live Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start pt-2">
        {/* Columna 1: Sidebar (3 cols) */}
        <div className="lg:col-span-3">
          <SettingsSidebar currentPath="/dashboard/settings/booking-page" />
        </div>

        {/* Columna 2: Editor de Configuración (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          {/* Estilo Visual */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-600" />
              <span>Estilo de presentación</span>
            </h3>

            <div className="grid grid-cols-2 gap-3">
              <div
                onClick={() => setStyle(1)}
                className={`p-3.5 rounded-2xl border-2 cursor-pointer transition-all ${
                  style === 1
                    ? 'border-emerald-600 bg-emerald-50/40 text-emerald-950 font-bold'
                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                }`}
              >
                <div className="text-xs font-bold mb-1">Estilo 1 · Clásico</div>
                <p className="text-[11px] text-slate-500 leading-tight">
                  Logo centrado, banners panorámicos y catálogo mosaico.
                </p>
              </div>

              <div
                onClick={() => setStyle(2)}
                className={`p-3.5 rounded-2xl border-2 cursor-pointer transition-all ${
                  style === 2
                    ? 'border-emerald-600 bg-emerald-50/40 text-emerald-950 font-bold'
                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                }`}
              >
                <div className="text-xs font-bold mb-1">Estilo 2 · Foto grande</div>
                <p className="text-[11px] text-slate-500 leading-tight">
                  Fondo hero full-bleed y tarjetas horizontales.
                </p>
              </div>
            </div>
          </div>

          {/* Modo Claro / Oscuro */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Tema base
            </h3>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setTheme('LIGHT')}
                className={`flex-1 py-2.5 px-4 rounded-xl border flex items-center justify-center gap-2 text-xs font-bold cursor-pointer transition ${
                  theme === 'LIGHT'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-500/20'
                    : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                }`}
              >
                <Sun className="w-4 h-4 text-amber-500" />
                <span>Claro</span>
              </button>

              <button
                type="button"
                onClick={() => setTheme('DARK')}
                className={`flex-1 py-2.5 px-4 rounded-xl border flex items-center justify-center gap-2 text-xs font-bold cursor-pointer transition ${
                  theme === 'DARK'
                    ? 'border-emerald-600 bg-slate-900 text-white ring-2 ring-emerald-500/20'
                    : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                }`}
              >
                <Moon className="w-4 h-4 text-slate-300" />
                <span>Oscuro</span>
              </button>
            </div>
          </div>

          {/* Color de Marca */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <Palette className="w-4 h-4 text-blue-600" />
              <span>Color de marca</span>
            </h3>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="w-10 h-10 rounded-xl cursor-pointer border border-slate-200 p-0.5 bg-white"
                />
                <input
                  type="text"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="w-28 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-800"
                />
                <span className="text-[11px] text-slate-400">
                  Calcula contraste WCAG automáticamente.
                </span>
              </div>

              {/* Swatches rápidos */}
              <div className="flex flex-wrap gap-2 pt-1">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c.hex}
                    type="button"
                    onClick={() => setBrandColor(c.hex)}
                    className="w-7 h-7 rounded-lg border border-black/10 transition-transform hover:scale-110 flex items-center justify-center"
                    style={{ backgroundColor: c.hex }}
                    title={c.name}
                  >
                    {brandColor === c.hex && <Check className="w-3.5 h-3.5 text-white drop-shadow" />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Activos Multimedia & Frase */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-purple-600" />
              <span>Imágenes y mensaje</span>
            </h3>

            <div className="space-y-4">
              {/* Logo de la marca */}
              <div className="space-y-1.5">
                <ImageUploadDropzone
                  value={logoUrl}
                  onChange={setLogoUrl}
                  aspect="square"
                  label="Logo de la marca"
                  helperText="Formato cuadrado o circular recomendado (PNG transparente o JPG)"
                />
              </div>

              {/* Portada principal */}
              <div className="space-y-1.5">
                <ImageUploadDropzone
                  value={coverUrl}
                  onChange={setCoverUrl}
                  aspect="cover"
                  label="Foto de portada principal"
                  helperText="Imagen panorámica de alta calidad que se mostrará en la cabecera"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-slate-700">
                    Frase de bienvenida
                  </label>
                  <span className="text-[10px] text-slate-400 font-medium">
                    {welcomePhrase.length}/160
                  </span>
                </div>
                <textarea
                  maxLength={160}
                  rows={2}
                  value={welcomePhrase}
                  onChange={(e) => setWelcomePhrase(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Botón Guardar */}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-[#0a3826] hover:bg-[#07291c] text-white rounded-2xl text-xs font-bold shadow-md hover:shadow-lg transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <Save className="w-4 h-4 text-emerald-400" />
            <span>{saving ? 'Guardando personalización...' : 'Guardar personalización'}</span>
          </button>
        </div>

        {/* Columna 3: Live Device Sandbox (4 cols) */}
        <div className="lg:col-span-4 sticky top-6 space-y-4">
          <div className="bg-white border border-slate-200/90 rounded-3xl p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <span className="text-xs font-bold text-slate-900">Previsualización en vivo</span>
              <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
                <button
                  type="button"
                  onClick={() => setPreviewDevice('mobile')}
                  className={`p-1.5 rounded-lg transition ${
                    previewDevice === 'mobile' ? 'bg-white shadow-2xs text-slate-900' : 'text-slate-400'
                  }`}
                  title="Celular"
                >
                  <Smartphone className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewDevice('desktop')}
                  className={`p-1.5 rounded-lg transition ${
                    previewDevice === 'desktop' ? 'bg-white shadow-2xs text-slate-900' : 'text-slate-400'
                  }`}
                  title="Computadora"
                >
                  <Monitor className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Frame Simulado */}
            <div
              className={`mx-auto rounded-3xl overflow-hidden border-4 border-slate-800 shadow-xl transition-all duration-300 ${
                previewDevice === 'mobile' ? 'w-[260px] h-[480px]' : 'w-full h-[320px]'
              }`}
              style={{
                backgroundColor: theme === 'DARK' ? '#0f172a' : '#f8fafc',
                color: theme === 'DARK' ? '#f8fafc' : '#0f172a'
              }}
            >
              {/* Top Notch Mobile */}
              {previewDevice === 'mobile' && (
                <div className="w-24 h-3.5 bg-slate-800 rounded-b-xl mx-auto" />
              )}

              <div className="p-4 flex flex-col items-center text-center space-y-3 overflow-y-auto h-full scrollbar-none">
                {/* Portada & Avatar */}
                <div className="relative w-full h-20 rounded-2xl overflow-hidden bg-slate-200 shrink-0">
                  <img src={coverUrl} alt="Portada" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/30" />
                </div>

                <div className="-mt-8 relative w-14 h-14 rounded-2xl border-2 border-white overflow-hidden shadow bg-slate-800 flex items-center justify-center font-black text-white text-xl">
                  {logoUrl ? <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" /> : 'J'}
                </div>

                <div>
                  <h4 className="text-sm font-extrabold">{businessName}</h4>
                  <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-2 px-2">
                    {welcomePhrase}
                  </p>
                </div>

                {/* Categorías simuladas */}
                <div className="w-full space-y-2 pt-1">
                  <div
                    className="p-2.5 rounded-xl text-white text-xs font-bold flex items-center justify-between shadow-xs"
                    style={{ backgroundColor: brandColor }}
                  >
                    <span>CORTE</span>
                    <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-md">2 turnos</span>
                  </div>
                  <div
                    className="p-2.5 rounded-xl text-white text-xs font-bold flex items-center justify-between opacity-90 shadow-xs"
                    style={{ backgroundColor: brandColor }}
                  >
                    <span>BARBA</span>
                    <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-md">1 turno</span>
                  </div>
                </div>

                <div className="mt-auto pt-2">
                  <span className="text-[9px] text-slate-500 font-semibold">
                    🛡️ Reservas verificadas · AppGenda
                  </span>
                </div>
              </div>
            </div>

            <Link
              href="/"
              target="_blank"
              className="w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition cursor-pointer"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span>Ver mi página pública en vivo</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
